import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { type LucideIcon } from 'lucide-react';
import { cn } from '@/utils/cn';

export interface NavItemProps {
  icon: LucideIcon;
  label: string;
  to: string;
  badge?: string;
  isCollapsed?: boolean;
  onClick?: () => void;
  className?: string;
}

export const NavItem: React.FC<NavItemProps> = ({
  icon: Icon,
  label,
  to,
  badge,
  isCollapsed = false,
  onClick,
  className,
}) => {
  const location = useLocation();
  const isActive = location.pathname === to;

  return (
    <Link
      to={to}
      onClick={onClick}
      className={cn(
        'group relative flex items-center px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200',
        'hover:bg-secondary-100/80 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1',
        isActive
          ? 'bg-primary-600 text-white shadow-md hover:bg-primary-700'
          : 'text-secondary-600 hover:text-secondary-900',
        isCollapsed ? 'justify-center w-12 h-12' : 'justify-start',
        className
      )}
      title={isCollapsed ? label : undefined}
    >
      <Icon
        className={cn(
          'w-5 h-5 transition-colors duration-200',
          !isCollapsed && 'mr-3',
          isActive ? 'text-white' : 'text-secondary-400 group-hover:text-secondary-600'
        )}
      />

      {!isCollapsed && (
        <>
          <span className="flex-1">{label}</span>
          {badge && (
            <span
              className={cn(
                'ml-2 px-2 py-0.5 text-xs rounded-full font-medium',
                isActive ? 'bg-white/20 text-white' : 'bg-primary-100 text-primary-700'
              )}
            >
              {badge}
            </span>
          )}
        </>
      )}
    </Link>
  );
};

export default NavItem;
